import { createSelector } from "@reduxjs/toolkit";
import { matchesFilterTokens, matchesSectionFilterTokens } from "@src/utils/sectionFilter.utils";
import { getThunkAndSlice } from "@src/store/getThunkAndSlice";
import { fromType } from "@src/store/helpers";
import { _selectMapSplitMinQuantity, _selectSelectedRowNames, _selectSelectedSectionNames } from "@src/store/map/mapSlice";
import { RootState } from "@src/store/store";

export interface TicketNetworkAPIResponse {
	TicketGroups: Array<TicketNetworkTicketGroup>;
	TotalCount: number;
}

export interface TicketNetworkTicketGroup {
	AvailableQuantity: number;
	DeliveryMethods: Array<string>;
	EventId: number;
	ExchangeTicketGroupId: number;
	Mine: boolean;
	OnHandDate: string;
	Notes: string;
	PurchasableQuantities: Array<number>;
	Seats: {
		Section: string;
		Row?: string;
		HighSeat?: string;
		LowSeat?: string;
	};
	UnitPrice: {
		FacePrice: {
			CurrencyCode?: string;
			Value: number;
		};
		RetailPrice: {
			CurrencyCode?: string;
			Value: number;
		};
		WholesalePrice: {
			CurrencyCode?: string;
			Value: number;
		};
	};
	NearTerm?: {
		NearTermDeliveryMethod: {
			Id?: number;
			Description: string;
		};
		NearTermDisplay: {
			Id?: number;
			Description: string;
		};
	};
	TicketGroupType: {
		Id: number;
		Description: string;
	};
}

export interface ActiveTicketNetworkListing {
	Section: string;
	Row: string;
	Quantity: number;
	Price: number;
	IsInstantDelivery: boolean;
	IsPDFAvailable: boolean;
	Description: string;
	TicketID: number;
	QuantitySplitOptions: Array<number>;
	DeliveryMethod: string;
	StockType: string;
	IsUserListing: boolean;
}

const initialState = fromType<TicketNetworkAPIResponse>({
	TicketGroups: [],
	TotalCount: 0,
});

export const { thunk: getTicketNetworkActiveListings, slice: getTicketNetworkActiveListingsSlice } = getThunkAndSlice<
	{
		eventId: number;
	},
	typeof initialState
>({
	initialState,
	path: ({ eventId }) => `/api/Marketplace/Mercury/Event/${eventId}/Listings`,
	cacheId: "eventId",
});

const selectActiveTicketNetworkListingsData = (state: RootState) => state.marketplace.ticketNetwork.activeTicketNetworkListings;

export const selectAggregatedTicketNetworkListings = createSelector(
	[selectActiveTicketNetworkListingsData, _selectSelectedSectionNames, _selectMapSplitMinQuantity, _selectSelectedRowNames],
	(activeTicketNetworkListings, selectedSections, minQuantity, selectedRows) => {
		const apiResponse = activeTicketNetworkListings?.data;
		const ticketGroups = Array.isArray(apiResponse?.TicketGroups) ? apiResponse.TicketGroups : [];

		// Transform Mercury API response to our internal format
		const transformedListings: Array<ActiveTicketNetworkListing> = ticketGroups.map((group) => ({
			Section: group.Seats.Section || "",
			Row: group.Seats.Row || "",
			Quantity: group.AvailableQuantity,
			Price: group.UnitPrice.RetailPrice.Value,
			IsInstantDelivery: group.DeliveryMethods.includes("MD"), // Mobile Delivery
			IsPDFAvailable: group.DeliveryMethods.includes("EM"), // Electronic Mail
			Description: group.Notes || "",
			TicketID: group.ExchangeTicketGroupId,
			QuantitySplitOptions: group.PurchasableQuantities,
			DeliveryMethod: group.DeliveryMethods.join(", "),
			StockType: group.TicketGroupType.Description,
			IsUserListing: group.Mine,
		}));

		const filteredListings =
			transformedListings.filter((x) => {
				const isRawSectionIncluded = selectedSections.length > 0 ? matchesSectionFilterTokens(selectedSections, x.Section, x.Row) : true;
				const isRawRowIncluded = selectedRows.length > 0 ? matchesFilterTokens(selectedRows, x.Row) : true;
				const isSplitMinQuantityIncluded = !minQuantity || x.QuantitySplitOptions.includes(+minQuantity);

				return isRawSectionIncluded && isSplitMinQuantityIncluded && isRawRowIncluded;
			}) || [];

		return {
			activeTicketNetworkLoading: activeTicketNetworkListings.loading,
			filteredActiveTicketNetworkListings: filteredListings,
			activeTicketNetworkListingTotalTickets: filteredListings?.reduce((acc, x) => acc + x.Quantity, 0),
		};
	},
);
